import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { Router } from '@angular/router';
import { EopAssistantComponent } from '../components/eop-assistant/eop-assistant.component';
import { PrvNextButtonsComponent } from '../components/prv-next-buttons/prv-next-buttons.component';
import { ContactInfoPopupComponent } from '../components/contact-info-popup/contact-info-popup.component';
import { PopupMessageComponent } from '../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../components/spinner/spinner.component';
import { IntroductionComponent } from '../components/introduction/introduction.component';
import { DeleteConfirmationComponent } from '../components/delete-confirmation/delete-confirmation.component';
import { ECorePlanningTeamType, EEopTemplates, ESectionTypes, getSectionNameFromRoute } from '../../shared/models/eop_types.enum';
import { Icons } from '../../shared/helpers/icons';
import { IContact, IUserInfo } from '../../shared/models/contact';
import { IDepartment } from '../../shared/models/department';
import { UserDataService } from '../../shared/services/userData.service';
import { ContactsService } from '../../shared/services/contact.service';
import { DepartmentsService } from '../../shared/services/department.service';
import { EopPlanSlug, getPlanSlugFromUrl } from '../eop-config/eop-page-config';

const ROUTE_TO_TEAM_TYPE: Record<string, ECorePlanningTeamType> = {
  'system-school-safety-planning-team': ECorePlanningTeamType['School Safety Planning Team'],
  'emergency-telephone-list': ECorePlanningTeamType['Emergency Telephone List']
};

const PLAN_TO_TEMPLATE: Record<EopPlanSlug, EEopTemplates> = {
  'al': EEopTemplates['AL EOP'],
  'ms': EEopTemplates['MS EOP'],
  'ga': EEopTemplates['GA EOP']
};

@Component({
  selector: 'system-school-safety-planning-team',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    EopAssistantComponent,
    PrvNextButtonsComponent,
    ContactInfoPopupComponent,
    PopupMessageComponent,
    SpinnerComponent,
    IntroductionComponent,
    DeleteConfirmationComponent
  ],
  templateUrl: './system-school-safety-planning-team.component.html',
  styleUrl: './system-school-safety-planning-team.component.css'
})
export class SystemSchoolSafetyPlanningTeamComponent implements OnInit {

  sectionType: ESectionTypes = ESectionTypes['System/School Safety Planning Team'];
  teamType: ECorePlanningTeamType = ECorePlanningTeamType['School Safety Planning Team'];
  template: EEopTemplates = EEopTemplates['AL EOP'];
  displayTitle: string = 'System/School Safety Planning Team';

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  searchQuery: string = '';
  contactInfo: IContact | null = null;
  selectedDepartmentId: number  = 0;
  fullContactsList: IContact[] = [];
  contactsList: IContact[] = [];
  allPlanningTeam: any[] =[];
  departmentsList: IDepartment[] = [];
  // This is your original server data (snapshot)
  initialAssignments: { collaborative_department: number; contact_id: number }[] = [];
  // This is the editable version that reflects the UI state
  editableAssignments: { collaborative_department: number; contact_id: number }[] = [];
  messageModal: string | null = null;
  popupMessage: string = '';
  showModal: string = '';

  // Several requests run in parallel on this page (contacts, positions, assignments).
  // Count them so the spinner stays up until the last one settles, instead of the first
  // response hiding it while the others are still in flight.
  private pendingRequests: number = 0;

  get loading(): boolean {
    return this.pendingRequests > 0;
  }

  private startLoading(): void {
    this.pendingRequests++;
  }

  private stopLoading(): void {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
  }
  loadingContacts: boolean = true;

  constructor(
    private router: Router,
    private userDataService: UserDataService,
    private contactsService: ContactsService,
    private departmentsService: DepartmentsService
  ) { }

  ngOnInit(): void {
    this.applyRouteContext();
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadContacts();
    this.loadDepartments();
  };

  private applyRouteContext(): void {
    const url = this.router.url.replace(/\/+$/, '');
    const segments = url.split('/');
    const lastSegment = segments[segments.length - 1] ?? '';

    this.template = PLAN_TO_TEMPLATE[getPlanSlugFromUrl(this.router.url)];
    this.teamType = ROUTE_TO_TEAM_TYPE[lastSegment] ?? ECorePlanningTeamType['School Safety Planning Team'];

    const sectionName = getSectionNameFromRoute(lastSegment);
    if (sectionName) {
      this.displayTitle = sectionName;
      this.sectionType = ESectionTypes[sectionName as keyof typeof ESectionTypes];
    }
  }

  loadBuildingAllPlanningTeam(): void {
    if (!this.currentBuildingId) return;
  
    this.startLoading();
  
    this.contactsService.getBuildingPlanningTeam(this.currentBuildingId, this.template, this.teamType).subscribe({
      next: (response) => {
        this.stopLoading();
  
        if (!response.has_error && response.data) {
          this.allPlanningTeam = response.data || [];
  
          this.initialAssignments = this.allPlanningTeam.length > 0
            ? this.allPlanningTeam.map(item => ({
                collaborative_department: item.collaborative_department,
                contact_id: item.contact_id
              }))
            : [];
            
          this.editableAssignments = [...this.initialAssignments];
          console.log("All Planning Team loaded successfully:", this.allPlanningTeam);
        } else {
          this.allPlanningTeam = [];
          this.initialAssignments = [];
          this.editableAssignments = [];
        }
      },
      error: (error: any) => {
        this.stopLoading();
        console.error('Error fetching all planning team:', error.message);
        
        this.allPlanningTeam = [];
        this.initialAssignments = [];
        this.editableAssignments = [];
      }
    });
  }  

  // load all Contacts and Contacts of a department
  loadContacts(): void {
    this.startLoading();
    this.contactsService.getContacts(this.currentBuildingId!).subscribe({
      next: (response) => {
        this.loadingContacts = false;
        this.stopLoading();
        this.fullContactsList = response.data || [];
        this.contactsList = [...this.fullContactsList];
        console.log('Contacts loaded successfully:', response);
        // Sort the list after retrieving contacts list.
        if (this.contactsList.length > 0) {
          this.contactsList.sort((a, b) => a.name.localeCompare(b.name));
        }
        this.loadBuildingAllPlanningTeam();
      },
      error: (error: any) => {
        this.stopLoading();
        this.loadingContacts = false;
        console.error('Error loading contacts:', error.message);
      }
    });
  };

  // Load Departments 
  loadDepartments(): void {
    this.startLoading();
    this.departmentsService.getDepartments(this.template, undefined, String(this.teamType)).subscribe({
      next: (response) => {
        this.stopLoading();
        this.departmentsList = response.data ? response.data : [];
        console.log('Departments loaded successfully:', response);
        if (this.departmentsList.length > 0) {
          this.selectedDepartmentId = this.departmentsList[0].id;
        }
      },
      error: (error: any) => {
        this.stopLoading();
        console.error('Error fetching departments:', error.message); 
      },
    });
  };  

  filterContacts(): void {
    const query = this.searchQuery.toLowerCase().trim();
  
    this.contactsList = this.fullContactsList.filter((item) => {
      return (
        item.name.toLowerCase().includes(query) ||
        item.Title?.toLowerCase().includes(query) ||
        item.workphone?.toLowerCase().includes(query) ||
        item.email?.toLowerCase().includes(query)
      );
    });
  };

  getAssignedContact(departmentId: number): IContact | null {
    const assignment = this.editableAssignments.find(
      a => a.collaborative_department === departmentId
    );
  
    if (!assignment || assignment.contact_id === 0) return null;
  
    return this.fullContactsList.find(c => c.id === assignment.contact_id) || null;
  }

  isDepartmentAssigned(departmentId: number): boolean {
    const a = this.editableAssignments.find(e => e.collaborative_department === departmentId);
    return !!a && a.contact_id !== 0;
  } 
  
  isDepartmentNotApplicable(departmentId: number): boolean {
    return this.editableAssignments.some(
      item => item.collaborative_department === departmentId && item.contact_id === 0
    );
  }  

  assignContactToDepartment(departmentId: number, contactId: number): void {
    // Remove existing assignment for this department
    this.editableAssignments = this.editableAssignments.filter(
      item => item.collaborative_department !== departmentId
    );
  
    // Add the new assignment
    this.editableAssignments.push({
      collaborative_department: departmentId,
      contact_id: contactId
    });
  }

  markNotApplicable(departmentId: number): void {
    const existing = this.editableAssignments.find(
      item => item.collaborative_department === departmentId
    );
  
    if (existing && existing.contact_id === 0) {
      // Already marked as not applicable, so remove it
      this.editableAssignments = this.editableAssignments.filter(
        item => item.collaborative_department !== departmentId
      );
    } else {
      // Remove any existing assignment
      this.editableAssignments = this.editableAssignments.filter(
        item => item.collaborative_department !== departmentId
      );
  
      // Add not applicable assignment
      this.editableAssignments.push({
        collaborative_department: departmentId,
        contact_id: 0
      });
    }
  }
  
  removeAssignment(departmentId: number): void {
    this.editableAssignments = this.editableAssignments.filter(
      item => item.collaborative_department !== departmentId
    );
  } 

  handleOpenModal(modalName: string, item?: any): void {
    this.showModal = modalName;
    if (modalName === 'contact-info' && item) {
      this.contactInfo = item;
    }
  }

  handleCloseModal(modalName: string): void {
    this.showModal = '';
    if (modalName === 'contact-info') {
      this.contactInfo = null;
    }
  }

  // save new changes in department.
  saveSelectedContacts(): void {
    if (this.currentBuildingId) {
      console.log('Saving assignments:', this.editableAssignments);
      this.startLoading();
      this.contactsService.saveBuildingCorePlanningTeam(this.currentBuildingId, this.template, this.teamType.toString(), this.editableAssignments).subscribe({
        next: (res) => {
          this.stopLoading();
            if (!res.has_error) {
              this.messageModal = 'success';
              this.popupMessage = `This occurred after saving ${this.displayTitle}.`;
              this.loadBuildingAllPlanningTeam();
            }
        },
        error: (error: any) => {
          this.stopLoading();
          console.error('Error saving assignments:', error.message);
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
        },
      })
    }
  }

  deleteBuildingCorePlanningTeam(): void {
    if (this.currentBuildingId) {
      this.startLoading();
      this.contactsService.deleteBuildingCorePlanningTeam(this.currentBuildingId, this.template, this.teamType.toString()).subscribe({
        next: (res) => {
          this.stopLoading();
            if (!res.has_error) {
              this.messageModal = 'success';
              this.popupMessage = `This occurred after deleting ${this.displayTitle}.`;
              this.showModal = '';
              this.loadBuildingAllPlanningTeam();
            }
        },
        error: (error: any) => {
          this.stopLoading();
          console.error('Error deleting all assignments:', error.message);
          this.showModal = '';
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
        },
      })
    }
  }

  approveBuildingCorePlanningTeam(): void {
    if (this.currentBuildingId) {
      this.startLoading();
      this.contactsService.approveBuildingCorePlanningTeam(this.currentBuildingId, this.template, this.teamType.toString()).subscribe({
        next: (res) => {
          this.stopLoading();
            if (!res.has_error) {
              this.messageModal = 'success';
              this.popupMessage = `This occurred after confirming ${this.displayTitle}.`;
              this.showModal = '';
              this.loadBuildingAllPlanningTeam();
            }
        },
        error: (error: any) => {
          this.stopLoading();
          console.error('Error confirming all assignments:', error.message);
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
        },
      })
    }
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  get isGaPlan(): boolean {
    return getPlanSlugFromUrl(this.router.url) === 'ga';
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }
}
